import { padZero } from './string';
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const DAY_MS = 86400000; // Un dia
export function parseShortDate(str) {
    const [dia, mes, anio] = str.split('/').map(s => parseInt(s, 10));
    return new Date(anio, mes - 1, dia);
}
export function parseDateTime(str) {
    const [date, time] = str.split(' ');
    const [dia, mes, anio] = date.split('/').map(s => parseInt(s, 10));
    const [hora, minutos, segundos] = time ? time.split(':').map(s => (s ? parseInt(s, 10) : 0)) : [0, 0, 0];
    return new Date(anio, mes - 1, dia, hora, minutos, segundos);
}
export function parseISO8601(str) {
    return new Date(str);
}
/**
 * Convierte un `Date` en `number`.
 * Si el valor ya es number o es undefined, se devuelve como esta.
 *
 * @param date
 */
export function dateToTs(date) {
    return date instanceof Date ? date.getTime() : date;
}
/**
 * Formatea una fecha como "dd/mm/yyyy", completando con ceros cuando el dia o el mes es < 10.
 *
 * @param {(Date | number)} date fecha de tipo Date o timestamp
 * @param includeTime flag que indica si incluir la hora.
 * @returns
 */
export function formatDate(date, opts = {}) {
    if (!date)
        return '';
    if (!(date instanceof Date))
        date = new Date(date);
    const sameYear = new Date().getFullYear() === date.getFullYear();
    const suffix = opts.includeTime ? ` - ${padZero(date.getHours(), 2)}:${padZero(date.getMinutes(), 2)}:${padZero(date.getSeconds(), 2)}` : '';
    const year = sameYear && opts.removeSameYear ? '' : '/' + date.getFullYear();
    return `${padZero(date.getDate(), 2)}/${padZero(date.getMonth() + 1, 2)}${year}${suffix}`;
}
/**
 * Formatea una fecha en un lenguaje mas "humano".
 * Si es hoy, el resultado sera 'hoy', si es el dia anterior sera 'ayer'.
 * En otros casos, si es el mismo año sera por ej '23 ene' y si es otro año '8 jul 16'.
 * @param date fecha a formatear.
 */
export function prettyFormatDate(date) {
    if (!date)
        return '';
    if (!(date instanceof Date))
        date = new Date(date);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sameYear = now.getFullYear() === date.getFullYear();
    // const sameMonth = now.getMonth() === date.getMonth();
    if (date.getTime() >= today.getTime() && date.getTime() < today.getTime() + DAY_MS)
        return 'hoy';
    if (date.getTime() >= today.getTime() - DAY_MS && date.getTime() < today.getTime())
        return 'ayer';
    const year = sameYear ? '' : ' ' + (date.getFullYear() > 2000 ? date.getFullYear() - 2000 : date.getFullYear());
    return `${date.getDate()} ${MONTHS[date.getMonth() - 1]}${year}`;
}
/**
 * Formatea una hora como "HH:MM:ss", completando con ceros cuando el dia o el mes es < 10.
 *
 * @param date fecha a formatear (Date o timestamp)
 * @param includeSeconds flag que indica si se incluyen los segundos.
 */
export function formatTime(date, includeSeconds = true) {
    if (!date)
        return '';
    if (!(date instanceof Date))
        date = new Date(date);
    const suffix = includeSeconds ? `:${padZero(date.getSeconds(), 2)}` : '';
    return `${padZero(date.getHours(), 2)}:${padZero(date.getMinutes(), 2)}${suffix}`;
}
/**
 * Formatea una duracion en formato 'HH:MM:ss'
 * Si la duracion es menos de un segundo, se la considera de 0.
 * @param ms valor de la duracion en ms.
 */
export function formatDuration(ms) {
    let seconds = Math.floor(ms / 1000);
    const hours = Math.floor(seconds / 3600);
    seconds -= hours * 3600;
    const minutes = Math.floor(seconds / 60);
    seconds -= minutes * 60;
    return padZero(hours, 2) + ':' + padZero(minutes, 2) + ':' + padZero(seconds, 2);
}
/**
 * Formatea una duracion en ms, con un formato de tipo '5d 12h 8m 24s'.
 * Si la duracion es menos de un segundo, el formato es de tipo '436 ms'.
 * Cuando alguno de los valores es cero, se lo EXCLUYE para hacer mas corto el texto (ej: '5d 15m')
 * @param ms valor de la duracion en ms.
 */
export function prettyFormatDuration(ms) {
    if (ms < 1000)
        return ms + ' ms';
    let seconds = Math.floor(ms / 1000);
    const days = Math.floor(seconds / 86400);
    seconds -= days * 86400;
    const hours = Math.floor(seconds / 3600);
    seconds -= hours * 3600;
    const minutes = Math.floor(seconds / 60);
    seconds -= minutes * 60;
    let result = '';
    if (seconds > 0 && hours === 0 && days === 0)
        result = seconds + 's ' + result;
    if (minutes > 0)
        result = minutes + 'm ' + result;
    if (hours > 0)
        result = hours + 'h ' + result;
    if (days > 0)
        result = days + 'd ' + result;
    return result.trim();
}
/**
 * Un año en milisegundos, util para obtener una edad
 */
export const YEAR_MS = 31556952000;
/**
 * Calcula la edad en base a su nacimiento, y opcionalmente, el deceso
 * @param birth fecha de nacimiento
 * @param death fecha de fallecimiento, si ocurrio
 */
export function calculateAge(birth, death = new Date()) {
    const age = death.getFullYear() - birth.getFullYear();
    const m = death.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && death.getDate() < birth.getDate()))
        return age - 1;
    return age;
}
/**
 * Valida que la fecha sea valida en formato "dd/mm/yyyy"
 * @param dateString string a validar.
 * @param separator caracter que separa los campos.
 */
export function isValidDate(dateString, separator = '/') {
    if (!dateString)
        return false;
    if (separator !== '/')
        dateString = dateString.replace(new RegExp(separator, 'g'), '/');
    // First check for the pattern
    if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString))
        return false;
    // Parse the date parts to integers
    var parts = dateString.split('/');
    var day = parseInt(parts[0], 10);
    var month = parseInt(parts[1], 10);
    var year = parseInt(parts[2], 10);
    // Check the ranges of month and year
    if (year < 1000 || year > 3000 || month == 0 || month > 12)
        return false;
    var monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    // Adjust for leap years
    if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0))
        monthLength[1] = 29;
    // Check the range of the day
    return day > 0 && day <= monthLength[month - 1];
}
//# sourceMappingURL=date.js.map